import { assignInlineVars } from '@vanilla-extract/dynamic';

import { Nil } from '@core';
import { GraphLineNode } from '@data';

import { colors } from '../../../colors';
import { Heading3 } from '../../Text';

import * as css from './FloorLegends.css';

import type { LegendPayload } from '../Graph.types';

const LEGEND_COUNT = 5;

/**
 * This is the floor legend rendered, it renders legends for
 * all all of the lines the user requested and also the name of the omitted lines as well.
 *
 * @param payload The Lines being rendered in Recharts format
 * @param omitNodes The lines being omitted from the rendering by the users layout customization
 * @returns The Legend for the floor in JSX
 */
export const renderFloorLegend = (
    payload?: LegendPayload[],
    omitNodes?: GraphLineNode[] | Nil,
    translate?: (key: string) => string,
) => {
    // How many empty nodes do we need to render MAX - payload - omit = Empty nodes
    const emptyCount = Math.max(0, LEGEND_COUNT - (payload?.length ?? 0) - (omitNodes?.length ?? 0));
    const emptyLegends = Array(emptyCount).fill(null);
    return (
        <ul className={css.legendLayout}>
            {payload &&
                payload.length > 0 &&
                payload.map((entry, index) => {
                    // Render custom label if it was provided
                    const value = entry.payload?.label || entry.value;
                    const type = entry.payload?.legendType || 'none';
                    const dashed = !!entry.payload?.strokeDasharray;

                    if (type === 'none') {
                        return null;
                    }

                    return (
                        <li key={`${entry.value}_${index}`} className={css.legend({ inactive: entry.inactive })}>
                            <Heading3 whiteSpace="nowrap" color="inverse">
                                {value}
                            </Heading3>
                            <div
                                className={css.legendLine({ dotted: dashed })}
                                style={assignInlineVars({
                                    [css.legendLineColor]: entry.color,
                                })}
                            />
                        </li>
                    );
                })}
            {omitNodes?.map((node, index) => (
                <li key={`_${index}`} className={css.legend({ inactive: true })}>
                    <Heading3 whiteSpace="nowrap" px={16} color="inverse">
                        {translate?.(node.i18n ?? '-')}
                    </Heading3>
                    <div
                        className={css.legendLine({ dotted: false })}
                        style={assignInlineVars({
                            [css.legendLineColor]: colors.bluegray[700],
                        })}
                    />
                </li>
            ))}
            {emptyLegends.map((_, index) => (
                <li key={`_${index}`} className={css.legend({ inactive: true })}>
                    <Heading3 whiteSpace="nowrap" px={16} color="inverse">
                        &#8212;
                    </Heading3>
                    <div
                        className={css.legendLine({ dotted: false })}
                        style={assignInlineVars({
                            [css.legendLineColor]: colors.bluegray[700],
                        })}
                    />
                </li>
            ))}
        </ul>
    );
};
